import React, { useEffect, useState } from "react"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import { filters as defaultFilters } from "components/Common/constants"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import DeleteModal from "components/Common/DeleteModal"
import ConfirmModal from "components/Common/ConfirmModal"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import {
  deleteBrand,
  getAllBrands,
  getAssignedBrands,
  setDefaultBrand,
  toggleBrand,
} from "store/actions/brandsAction"
import {
  saDeleteBrand,
  saGetAllBrands,
  saToggleBrand,
} from "store/actions/superAdminAction"

const BRAND_SCOPE_CONFIG = {
  tenant: {
    permissionCategory: 52,
    permissions: {
      view: "brands_1",
      create: "brands_2",
      edit: "brands_3",
      delete: "brands_4",
    },
    routes: {
      list: "/brands",
      add: "/brands/add",
      edit: "/brands/edit",
      details: "/brands/brand-details",
    },
    fallbackRoute: "/dashboard",
    listTitleKey: "Brands List",
    actions: {
      getAllBrands,
      deleteBrand,
      toggleBrand,
    },
    viewOnly: true,
  },
  superadmin: {
    permissionCategory: 108,
    permissions: {
      view: "sa_brands_1",
      create: "sa_brands_2",
      edit: "sa_brands_3",
      delete: "sa_brands_4",
    },
    routes: {
      list: "/superadmin/brands",
      add: "/superadmin/brands/add",
      edit: "/superadmin/brands/edit",
      details: "/superadmin/brands/brand-details",
    },
    fallbackRoute: "/superadmin/dashboard",
    listTitleKey: "Brand Management",
    actions: {
      getAllBrands: saGetAllBrands,
      deleteBrand: saDeleteBrand,
      toggleBrand: saToggleBrand,
    },
    viewOnly: false,
  },
}

const Brands = ({ scope = "tenant" }) => {
  const config = BRAND_SCOPE_CONFIG[scope] || BRAND_SCOPE_CONFIG.tenant
  const { t } = useTranslation()
  document.title = t(`${config.listTitleKey} `)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page"), 10) || 1

  const {
    loading,
    isLoading,
    brands,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    defaultBrandId,
  } = useSelector(state => state.Brands)

  const { userRole, accountType } = useSelector(state => state.Login)

  const [permissions, setPermissions] = useState({
    list: false,
    create: false,
    edit: false,
    delete: false,
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState({
    status: 2,
    sortOrder: 2,
  })
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [showDefaultBrandModal, setShowDefaultBrandModal] = useState(false)
  const [brandToSetDefault, setBrandToSetDefault] = useState(null)
  const [isSettingDefault, setIsSettingDefault] = useState(false)

  useEffect(() => {
    const list = isPermissionEnabled(
      userRole,
      config.permissionCategory,
      config.permissions.view,
      accountType
    )
    const create =
      !config.viewOnly &&
      isPermissionEnabled(
        userRole,
        config.permissionCategory,
        config.permissions.create,
        accountType
      )
    const edit =
      !config.viewOnly &&
      isPermissionEnabled(
        userRole,
        config.permissionCategory,
        config.permissions.edit,
        accountType
      )
    const del =
      !config.viewOnly &&
      isPermissionEnabled(
        userRole,
        config.permissionCategory,
        config.permissions.delete,
        accountType
      )

    if (!list) {
      navigate(config.fallbackRoute)
      return
    }

    setPermissions({ list, create, edit, delete: del })
    if (scope === "tenant") {
      dispatch(getAssignedBrands())
    }
    fetchBrands(pageNumber)
  }, [userRole, accountType, navigate, scope])

  const fetchBrands = (page = 1) => {
    const { status, sortOrder } = selectedFilters
    dispatch(
      config.actions.getAllBrands({
        perPage: itemsPerPage || 10,
        pageNo: page,
        status: Number(status),
        orderBy: Number(sortOrder),
        keyword: searchTerm || "",
      })
    )
  }

  const handleSearch = () => {
    fetchBrands(1)
    navigate(`${config.routes.list}?page=1`)
  }

  const onPageChange = page => {
    fetchBrands(page)
    navigate(`${config.routes.list}?page=${page}`)
  }

  const handleReset = () => {
    setSearchTerm("")
    setSelectedFilters({ status: 2, sortOrder: 2 })
    dispatch(
      config.actions.getAllBrands({
        perPage: itemsPerPage || 10,
        pageNo: 1,
        status: 2,
        orderBy: 2,
        keyword: "",
      })
    )
    navigate(`${config.routes.list}?page=1`)
  }

  const handleToggleActive = item => {
    if (!permissions.edit) return
    setSelectedId(item._id)
    dispatch(config.actions.toggleBrand({ id: item._id }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          fetchBrands(currentPage || pageNumber)
        } else {
          dispatch(showAlert({ text: msg || t("Something went wrong"), type: "error" }))
        }
      })
      .finally(() => setSelectedId(null))
  }

  const openDefaultBrandModal = item => {
    if (scope !== "tenant") return
    if (String(item._id) === String(defaultBrandId)) return
    setBrandToSetDefault(item)
    setShowDefaultBrandModal(true)
  }

  const closeDefaultBrandModal = () => {
    if (isSettingDefault) return
    setShowDefaultBrandModal(false)
    setBrandToSetDefault(null)
  }

  const handleConfirmDefaultBrand = () => {
    if (!brandToSetDefault?._id) return
    setIsSettingDefault(true)
    dispatch(setDefaultBrand({ brandId: brandToSetDefault._id }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDefaultBrandModal(false)
          setBrandToSetDefault(null)
        } else {
          dispatch(
            showAlert({ text: msg || t("Something went wrong"), type: "error" })
          )
        }
      })
      .finally(() => setIsSettingDefault(false))
  }

  const handleDelete = () => {
    dispatch(config.actions.deleteBrand({ id: itemIdToDelete }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
          fetchBrands(currentPage || pageNumber)
        } else {
          dispatch(showAlert({ text: msg || t("Something went wrong"), type: "error" }))
        }
      })
  }

  const getActionItems = item => {
    const items = [
      {
        key: "view",
        content: (
          <>
            <i className="bx bx-show"></i>
            {t("View Details")}
          </>
        ),
        onClick: () => navigate(`${config.routes.details}?id=${item._id}`),
      },
    ]

    if (permissions.edit) {
      items.push({
        key: "edit",
        content: (
          <>
            <i className="bx bx-edit-alt"></i>
            {t("Edit")}
          </>
        ),
        onClick: () => navigate(`${config.routes.edit}?id=${item._id}`),
      })
    }

    if (permissions.delete) {
      items.push({
        key: "delete",
        content: (
          <>
            <i className="bx bx-trash text-danger"></i>
            {t("Delete")}
          </>
        ),
        onClick: () => {
          setItemIdToDelete(item._id)
          setShowDeleteModal(true)
        },
      })
    }

    return items
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t(config.listTitleKey)}
                isAddBtn={!loading && permissions.create}
                addBtnText={t("Add Brand")}
                onAddBtnClick={() => navigate(config.routes.add)}
              />
              <CardBody>
                <Filters
                  filters={defaultFilters}
                  searchTerm={searchTerm}
                  handleSearchChange={e => setSearchTerm(e.target.value)}
                  handleFilterChange={(value, name) =>
                    setSelectedFilters(prev => ({ ...prev, [name]: value }))
                  }
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Search by name")}
                />

                {loading ? (
                  <TableShimmer row={7} col={scope === "tenant" ? 6 : 5} />
                ) : brands.length === 0 ? (
                  <p className="text-center my-3">{t("No brands found")}</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Brand Name")}</th>
                          <th>{t("Integration")}</th>
                          <th>{t("Status")}</th>
                          {scope === "tenant" && <th>{t("Default")}</th>}
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brands.map((item, index) => {
                          const isDefaultBrand =
                            scope === "tenant" &&
                            String(item._id) === String(defaultBrandId)
                          return (
                          <tr key={item._id}>
                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                            <td className="fw-semibold">
                              {item.brandName || "-"}
                              {isDefaultBrand && (
                                  <Badge color="primary" className="ms-2">
                                    {t("Default")}
                                  </Badge>
                                )}
                            </td>
                            <td>
                              {item.integration
                                ? t(item.integration, { defaultValue: item.integration })
                                : "-"}
                            </td>
                            <td>
                              {permissions.edit ? (
                                <Switch
                                  onClickHandler={() => handleToggleActive(item)}
                                  isChecked={item.status === 1}
                                  disabled={selectedId === item._id && isLoading}
                                />
                              ) : (
                                <Badge color={item.status === 1 ? "success" : "secondary"}>
                                  {item.status === 1 ? t("Active") : t("Inactive")}
                                </Badge>
                              )}
                            </td>
                            {scope === "tenant" && (
                              <td>
                                <Switch
                                  onClickHandler={() => openDefaultBrandModal(item)}
                                  isChecked={isDefaultBrand}
                                  disabled={
                                    isDefaultBrand ||
                                    (isSettingDefault &&
                                      brandToSetDefault?._id === item._id)
                                  }
                                />
                              </td>
                            )}
                            <td>
                              <TableActionsDropdown items={getActionItems(item)} />
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {!loading && brands.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={brands}
                      totalCount={totalCount}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={onPageChange}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Brand"),
        })}
      />

      <ConfirmModal
        show={showDefaultBrandModal}
        isLoading={isSettingDefault}
        onDeleteClick={handleConfirmDefaultBrand}
        onCloseClick={closeDefaultBrandModal}
        buttonText={t("Confirm")}
        bodyTitle={t(
          "Are you sure you want to mark {{name}} as the default brand?",
          {
            name: brandToSetDefault?.brandName || t("Brand"),
          }
        )}
      />
    </div>
  )
}

export default Brands
